import { Center, Loader, Stack, Text, Title } from "@mantine/core";
import * as React from "react";
import { ErrorCard } from "@/components/error-card";
import { useLinkPatreonAccount } from "@/api/fapinstructor/patreon/link-patreon-account";
import { useNavigate } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Page } from "@/components/page";

const PatreonSearchSchema = z.object({
  code: z.string(),
});

export const Route = createFileRoute("/_authenticated/patreon/")({
  validateSearch: zodValidator(PatreonSearchSchema),
  component: PatreonRouteComponent,
});

function PatreonRouteComponent() {
  const { code } = Route.useSearch();
  const linkPatreonAccountMutation = useLinkPatreonAccount();

  React.useEffect(() => {
    if (linkPatreonAccountMutation.isIdle && code) {
      linkPatreonAccountMutation.mutate(code);
    }
  }, [code, linkPatreonAccountMutation]);

  return (
    <Page title="Patreon">
      <Center h="100%">
        <Stack ta="center" gap="sm">
          {linkPatreonAccountMutation.isPending ? (
            <LinkingPatreonAccount />
          ) : null}
          {linkPatreonAccountMutation.isSuccess ? (
            <SuccessLinkingPatreonAccount />
          ) : null}
        </Stack>
        {linkPatreonAccountMutation.isError ? (
          <ErrorCard
            title={<UnableToLinkTitle />}
            message={linkPatreonAccountMutation.error.message}
          />
        ) : null}
      </Center>
    </Page>
  );
}

function UnableToLinkTitle() {
  return (
    <>
      Unable to link your{" "}
      <Text inherit span c="orange">
        Patreon
      </Text>{" "}
      account
    </>
  );
}

function SuccessLinkingPatreonAccount() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const redirectTimeout = setTimeout(() => {
      navigate({ to: "/" });
    }, 3000);

    return () => {
      clearTimeout(redirectTimeout);
    };
  }, [navigate]);

  return (
    <>
      <Title>
        Successfully linked your{" "}
        <Text inherit span c="orange">
          Patreon
        </Text>{" "}
        Account
      </Title>
      <>
        <Text size="lg">Redirecting to the home screen...</Text>
        <Center>
          <Loader size="xl" type="dots" />
        </Center>
      </>
    </>
  );
}

function LinkingPatreonAccount() {
  return (
    <>
      <Title>
        Linking your{" "}
        <Text inherit span c="orange">
          Patreon
        </Text>{" "}
        Account
      </Title>
      <>
        <Text size="lg">
          Please wait while we link your account to Patreon...
        </Text>
        <Center>
          <Loader size="xl" type="dots" />
        </Center>
      </>
    </>
  );
}
